import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";

const WORD = "SPECIFIC";
const TOTAL_STEPS = 8;

const HIGHLIGHT_RANGES = {
  1: [0, 2],
  2: [1, 3],
  3: [0, 3],
  4: [3, 5],
  5: [0, 5],
  6: [5, 8],
  7: [0, 8],
};

const W = (red, prefix = "", suffix = "") => ({ prefix, red, suffix });

const STEP_INTROS = {
  1: "Say it, then trim it down:",
  2: "Now reduce the vowel sound:",
  3: "Now combine them:",
  4: "Isolate the vowel sound:",
  5: "Connect the first two parts:",
  6: "Move through the ending sound:",
  7: "Build the full word, piece by piece:",
};

const STEP_SEQUENCES = {
  1: [W("Sp", "", "eed"), W("Sp", "", "eak"), W("Sp", "", "ee"), W("Sp", "", "in"), W("Sp", "", "i"), W("Sp")],
  2: [W("Pe", "", "n"), W("Pu", "", "n"), W("Pu")],
  3: [W("Spe", "", "n"), W("Spu", "", "n"), W("Spu")],
  4: [W("Si", "", "t"), W("Si", "", "ng"), W("Si", "", "n"), W("Si")],
  5: [W("Spu"), W("Si"), W("Spusi")],
  6: [W("ick", "L"), W("ick", "T"), W("Fick")],
  7: [W("Spu"), W("Si"), W("Fick"), W("Spusifick")],
};

const fadeSlide = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};
const transition = { duration: 0.4, ease: [0.22, 1, 0.36, 1] };

const MainWord = ({ step }) => {
  if (step === 8) {
    return (
      <div className="flex items-baseline justify-center gap-1">
        <span className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">SPE</span>
        <span className="font-display text-2xl font-bold text-slate-400 sm:text-3xl">-</span>
        <span className="font-display text-4xl font-black text-red-600 sm:text-5xl">CI</span>
        <span className="font-display text-2xl font-bold text-slate-400 sm:text-3xl">-</span>
        <span className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">FIC</span>
      </div>
    );
  }
  const [start, end] = HIGHLIGHT_RANGES[step] || [-1, -1];
  return (
    <div className="font-display flex justify-center text-3xl font-bold tracking-wide sm:text-4xl">
      {WORD.split("").map((ch, i) => (
        <span key={i} className={i >= start && i < end ? "text-red-600" : "text-slate-900"}>
          {ch}
        </span>
      ))}
    </div>
  );
};

const SequenceWord = ({ word, size = "lg" }) => (
  <span
    className={
      size === "sm"
        ? "font-display text-base font-bold sm:text-lg"
        : "font-display text-2xl font-bold sm:text-3xl"
    }
  >
    {word.prefix && <span className="text-slate-900">{word.prefix}</span>}
    <span className="text-red-600">{word.red}</span>
    {word.suffix && <span className="text-slate-900">{word.suffix}</span>}
  </span>
);

const CheckmarkButton = ({ onConfirm }) => {
  const [checked, setChecked] = useState(false);

  const handleClick = () => {
    if (checked) return;
    setChecked(true);
    setTimeout(() => onConfirm(), 420);
  };

  return (
    <div className="relative flex h-9 w-9 items-center justify-center">
      <AnimatePresence>
        {checked && (
          <motion.span
            initial={{ scale: 0.6, opacity: 0.55 }}
            animate={{ scale: 1.9, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="absolute h-9 w-9 rounded-full bg-emerald-400"
          />
        )}
      </AnimatePresence>
      <motion.button
        type="button"
        data-testid="sisera-demo-checkmark-button"
        onClick={handleClick}
        whileTap={{ scale: 0.88 }}
        whileHover={{ scale: checked ? 1 : 1.06 }}
        animate={{ scale: checked ? [1, 1.15, 1] : 1 }}
        transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
        className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors duration-300 ${
          checked
            ? "border-emerald-500 bg-emerald-500"
            : "border-emerald-300 bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-100"
        }`}
        aria-label="Mark sound as practiced"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none">
          <path
            d="M5 13l4 4L19 7"
            stroke={checked ? "#ffffff" : "#10b981"}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </motion.button>
    </div>
  );
};

const TriangleStressVisualizer = () => (
  <div data-testid="triangle-stress-visualizer" className="mt-3 flex flex-col items-center">
    <div className="flex items-baseline justify-center gap-2">
      <span className="font-display text-xl font-semibold text-slate-500 sm:text-2xl">spu</span>
      <span className="font-display text-xl font-semibold text-slate-300 sm:text-2xl">-</span>
      <motion.span
        initial={{ scale: 1 }}
        animate={{ scale: [1, 1.4, 1.12] }}
        transition={{ duration: 0.55, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="font-display inline-block text-3xl font-black text-red-600 sm:text-4xl"
      >
        SI
      </motion.span>
      <span className="font-display text-xl font-semibold text-slate-300 sm:text-2xl">-</span>
      <span className="font-display text-xl font-semibold text-slate-500 sm:text-2xl">fick</span>
    </div>

    <svg viewBox="0 0 260 66" className="mt-1 h-[58px] w-64 sm:w-72" fill="none">
      <motion.path
        d="M22,52 L130,10 L238,52"
        stroke="#0284c7"
        strokeWidth="2.5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.5, ease: "easeInOut" }}
      />
      <motion.circle
        cx="22"
        cy="52"
        r="4"
        fill="#0284c7"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, delay: 0.02 }}
      />
      <motion.circle
        cx="130"
        cy="10"
        r="8"
        fill="#0284c7"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.35, delay: 0.68, ease: [0.34, 1.56, 0.64, 1] }}
      />
      <motion.circle
        cx="238"
        cy="52"
        r="4"
        fill="#0284c7"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, delay: 1.4 }}
      />
    </svg>
  </div>
);

const BackArrowButton = ({ onClick }) => (
  <motion.button
    type="button"
    data-testid="sisera-demo-back-button"
    onClick={onClick}
    whileHover={{ scale: 1.08, x: -1 }}
    whileTap={{ scale: 0.9 }}
    aria-label="Go back to previous step"
    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 transition-colors duration-200 hover:border-sky-300 hover:text-sky-600"
  >
    <ArrowLeft size={15} strokeWidth={2.4} />
  </motion.button>
);

export const SiseraMethodDemo = () => {
  const [step, setStep] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [sequenceDone, setSequenceDone] = useState(false);
  const [history, setHistory] = useState([]);
  const [expandedSteps, setExpandedSteps] = useState([]);

  const sequence = STEP_SEQUENCES[step];
  const hasSequence = Boolean(sequence);

  const buildHistoryEntry = (completedStep) => {
    const seq = STEP_SEQUENCES[completedStep];
    if (!seq) {
      return {
        step: completedStep,
        label: "spu-SI-fick (Triangle Stress)",
        words: [W("SI", "spu-", "-fick")],
      };
    }
    const sound = seq[seq.length - 1].red;
    const first = seq[0];
    const anchor = `${first.prefix}${first.red}${first.suffix}`;
    return { step: completedStep, label: `${sound} (${anchor})`, words: seq };
  };

  const handleNext = () => {
    if (step === TOTAL_STEPS) {
      setHistory([]);
      setExpandedSteps([]);
      setStep(0);
      setSubIndex(0);
      setSequenceDone(false);
      return;
    }
    if (step > 0) {
      setHistory((prev) => [...prev, buildHistoryEntry(step)]);
    }
    setStep((prev) => prev + 1);
    setSubIndex(0);
    setSequenceDone(false);
  };

  const handleBackWord = () => {
    const seq = STEP_SEQUENCES[step];
    if (seq && subIndex > 0) {
      setSubIndex((prev) => prev - 1);
      return;
    }
    if (step <= 1) {
      setStep(0);
      setSubIndex(0);
      setSequenceDone(false);
      return;
    }
    const prevStep = step - 1;
    const prevSeq = STEP_SEQUENCES[prevStep];
    setHistory((prev) => prev.slice(0, -1));
    setExpandedSteps((prev) => prev.filter((s) => s !== prevStep));
    setStep(prevStep);
    setSubIndex(prevSeq ? prevSeq.length - 1 : 0);
    setSequenceDone(false);
  };

  const toggleHistoryItem = (targetStep) => {
    setExpandedSteps((prev) =>
      prev.includes(targetStep) ? prev.filter((s) => s !== targetStep) : [...prev, targetStep]
    );
  };

  const handleCheckmark = () => {
    if (subIndex < sequence.length - 1) {
      setSubIndex((prev) => prev + 1);
    } else {
      setSequenceDone(true);
    }
  };

  const showNextButton = !hasSequence || sequenceDone;
  const buttonLabel =
    step === 0 ? "Try the Interactive Demo" : step === TOTAL_STEPS ? "Replay Demo" : "Next Step →";

  return (
    <motion.div
      layout
      id="sound-mapping-demo-card"
      data-testid="sisera-method-demo"
      className="pulse-border-glow mx-auto flex w-full max-w-md flex-col items-center rounded-[28px] border border-slate-200/80 bg-white/85 p-8 backdrop-blur-sm"
    >
      <h3 className="font-display text-center text-lg font-bold leading-snug text-slate-900 sm:text-xl">
        Drew’s Sound Ladder in Action: <br className="hidden sm:block" />
        Mastering “Specific”
      </h3>

      <div className="mt-4 flex w-full gap-1.5" data-testid="sisera-demo-progress-bar">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
          const segmentIndex = i + 1;
          let fill = 0;
          if (step > segmentIndex) {
            fill = 1;
          } else if (step === segmentIndex) {
            const seq = STEP_SEQUENCES[step];
            fill = !seq ? 1 : sequenceDone ? 1 : subIndex / seq.length;
          }
          return (
            <div key={i} className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
              <motion.div
                className="h-full rounded-full bg-sky-500"
                initial={false}
                animate={{ width: `${fill * 100}%` }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          );
        })}
      </div>

      <div
        data-testid="sisera-demo-main-word"
        className="mt-8 flex h-14 w-full items-center justify-center"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={`word-${step}`}
            initial={fadeSlide.initial}
            animate={fadeSlide.animate}
            exit={fadeSlide.exit}
            transition={transition}
          >
            <MainWord step={step === 0 ? -1 : step} />
          </motion.div>
        </AnimatePresence>
      </div>

      <div
        data-testid="sisera-demo-instruction"
        className={`mt-8 flex w-full flex-col items-center justify-center px-2 ${
          step === 8 ? "min-h-[10rem]" : "min-h-[7rem]"
        }`}
      >
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.p
              key="idle"
              initial={fadeSlide.initial}
              animate={fadeSlide.animate}
              exit={fadeSlide.exit}
              transition={transition}
              className="font-body text-center text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              Ready to see how one hard word gets built, sound by sound?
            </motion.p>
          )}

          {step === 8 && (
            <motion.div
              key="triangle"
              initial={fadeSlide.initial}
              animate={fadeSlide.animate}
              exit={fadeSlide.exit}
              transition={transition}
              className="flex flex-col items-center"
            >
              <p className="font-body text-center text-base leading-relaxed text-slate-600 sm:text-lg">
                Add the Triangle Stress:
              </p>
              <div className="mt-1 flex items-center gap-3">
                <BackArrowButton onClick={handleBackWord} />
                <TriangleStressVisualizer />
              </div>
            </motion.div>
          )}

          {hasSequence && !sequenceDone && (
            <motion.div
              key={`seq-${step}-${subIndex}`}
              initial={fadeSlide.initial}
              animate={fadeSlide.animate}
              exit={fadeSlide.exit}
              transition={transition}
              className="flex flex-col items-center gap-6"
            >
              <p className="font-body text-center text-sm text-slate-500 sm:text-base">
                {STEP_INTROS[step]}
              </p>
              <div className="flex items-center gap-4">
                <BackArrowButton onClick={handleBackWord} />
                <SequenceWord word={sequence[subIndex]} />
                <CheckmarkButton onConfirm={handleCheckmark} />
              </div>
            </motion.div>
          )}

          {hasSequence && sequenceDone && (
            <motion.div
              key={`seq-done-${step}`}
              initial={fadeSlide.initial}
              animate={fadeSlide.animate}
              exit={fadeSlide.exit}
              transition={transition}
              className="flex flex-col items-center gap-2"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <p className="font-body text-center text-base font-medium text-slate-600 sm:text-lg">
                Nice work! That sound is locked in.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {history.length > 0 && (
        <motion.div
          layout
          data-testid="sisera-demo-history"
          className="mt-2 flex w-full flex-col gap-2 border-t border-slate-100 pt-5"
        >
          <p className="font-mono-accent text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Your Progress Ladder
          </p>
          <AnimatePresence initial={false}>
            {history.map((item) => {
              const isExpanded = expandedSteps.includes(item.step);
              const reversedWords = [...item.words].reverse();
              return (
                <motion.div
                  key={item.step}
                  layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={transition}
                  data-testid={`sisera-demo-history-item-${item.step}`}
                  className="overflow-hidden rounded-xl bg-slate-50"
                >
                  <button
                    type="button"
                    onClick={() => toggleHistoryItem(item.step)}
                    data-testid={`sisera-demo-history-toggle-${item.step}`}
                    className="flex w-full items-center justify-between px-3.5 py-2"
                  >
                    <span className="font-mono-accent text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
                      Step {item.step}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-display text-sm font-semibold text-slate-500">
                        {item.label}
                      </span>
                      <motion.span
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.25 }}
                        className="text-slate-400"
                      >
                        <ChevronDown size={14} />
                      </motion.span>
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key="drilldown"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={transition}
                        className="overflow-hidden"
                      >
                        <div
                          data-testid={`sisera-demo-history-words-${item.step}`}
                          className="flex flex-col gap-1.5 px-3.5 pb-3 pt-1"
                        >
                          {reversedWords.map((word, idx) => (
                            <div
                              key={`${item.step}-${idx}`}
                              className={`flex items-center gap-2 rounded-lg px-2 py-1 ${
                                idx === 0 ? "bg-white" : ""
                              }`}
                            >
                              <span className="w-1.5 flex-shrink-0 rounded-full bg-slate-200" style={{ height: idx === 0 ? "16px" : "6px" }} />
                              <SequenceWord word={word} size="sm" />
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {showNextButton ? (
        step === 0 ? (
          <div className="chasing-light-btn mt-7 w-full">
            <Button
              data-testid="sisera-demo-next-button"
              onClick={handleNext}
              className="font-display h-11 w-full rounded-2xl bg-slate-900 text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-sky-600"
            >
              {buttonLabel}
            </Button>
          </div>
        ) : (
          <Button
            data-testid="sisera-demo-next-button"
            onClick={handleNext}
            className="font-display mt-7 h-11 w-full rounded-2xl bg-slate-900 text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-sky-600"
          >
            {buttonLabel}
          </Button>
        )
      ) : (
        <div className="mt-7 h-11 w-full" data-testid="sisera-demo-next-button-hidden" />
      )}

      <p className="font-body mt-5 text-center text-xs leading-relaxed text-slate-400">
        This is how Sound Ladder helps you connect difficult words to sounds you
        already know.
      </p>
    </motion.div>
  );
};
